import { Button, FormControl, FormHelperText, Stack, useThemeProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  FORM_CHIP_GROUP_THEME_NAME,
  type FormChipSelectedLook,
} from '@onevision-resources/design-theme';
import { useId } from 'react';
import {
  Controller,
  type FieldValues,
  type RegisterOptions,
  useFormContext,
} from 'react-hook-form';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import type { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { useWatchConditions } from 'src/components/Forms/use-validations';
import { ChoiceChip } from './form-chip-group.elements';

type ChipValue = string | number;

export interface FormChipGroupOption<T extends ChipValue> {
  id: string;
  value: T;
  label: string;
  testId?: string;
}

interface FormChipGroupBaseProps<T extends ChipValue> {
  label?: string;
  options: FormChipGroupOption<T>[];
  disabled?: boolean;
  sx?: SxProps<Theme>;
  /** Defaults to the surrounding theme: a secondary tint under V2, solid primary elsewhere. */
  selectedChipLook?: FormChipSelectedLook;
  'data-testid'?: string;
}

interface SingleSelection<T extends ChipValue> {
  multiple?: false;
  clearable?: false;
  value?: T;
  /** Also fires when the already selected chip is pressed again, with the same value. */
  onChange?: (value: T) => void;
}

interface MultiSelection<T extends ChipValue> {
  multiple: true;
  clearable?: boolean;
  value?: T[];
  onChange?: (values: T[]) => void;
}

type Selection<T extends ChipValue> = SingleSelection<T> | MultiSelection<T>;

interface RhfBinding {
  name: string;
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  required?: boolean | string;
  showErrorText?: boolean;
  showsIf?: CommonFormFieldProps<string, unknown>['showsIf'];
}

interface ControlledBinding<T extends ChipValue> {
  name?: undefined;
  value: T | T[] | null | undefined;
}

/**
 * Deliberately narrow, like `FormToggleGroup`, and not part of the `DynamicForm`
 * `FormField` union. Bound to react-hook-form when `name` is set (inside a
 * `FormProvider` / `GenericForm`), controlled by `value` + `onChange` otherwise.
 */
export type FormChipGroupProps<T extends ChipValue> = FormChipGroupBaseProps<T> &
  Selection<T> &
  (RhfBinding | ControlledBinding<T>);

type BoundChipGroupProps<T extends ChipValue> = FormChipGroupBaseProps<T> &
  Selection<T> &
  RhfBinding;

type ControlledChipGroupProps<T extends ChipValue> = FormChipGroupBaseProps<T> &
  Selection<T> &
  ControlledBinding<T>;

function getSelectedValues<T extends ChipValue>(
  options: FormChipGroupOption<T>[],
  value: unknown,
): T[] {
  const picked: unknown[] = Array.isArray(value) ? value : [value];

  return options
    .map((option) => option.value)
    .filter((optionValue) => picked.includes(optionValue));
}

function emitChange<T extends ChipValue>(selection: Selection<T>, next: T[]) {
  if (selection.multiple) {
    selection.onChange?.(next);
    return;
  }

  const [first] = next;
  if (first !== undefined) {
    selection.onChange?.(first);
  }
}

function getChipIconName(isSelected: boolean, multiple: boolean) {
  if (isSelected) {
    return 'done';
  }

  return multiple ? 'add' : undefined;
}

interface ChipGroupViewProps<T extends ChipValue> extends FormChipGroupBaseProps<T> {
  selected: T[];
  multiple: boolean;
  clearable: boolean;
  onSelect: (next: T[]) => void;
  errorText?: string;
}

const ChipGroupView = <T extends ChipValue>({
  label,
  options,
  disabled,
  sx,
  selectedChipLook = 'filled',
  'data-testid': testId,
  selected,
  multiple,
  clearable,
  onSelect,
  errorText,
}: ChipGroupViewProps<T>) => {
  const labelId = useId();

  const toggle = (value: T) => {
    if (disabled) {
      return;
    }

    if (!multiple) {
      onSelect([value]);
      return;
    }

    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    onSelect(options.map((option) => option.value).filter((item) => next.includes(item)));
  };

  return (
    <FormControl error={!!errorText} disabled={disabled} sx={sx} data-testid={testId}>
      {!!label && (
        <FormFieldLabel id={labelId} disabled={disabled}>
          {label}
        </FormFieldLabel>
      )}
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        gap={0.75}
        role="group"
        aria-labelledby={label ? labelId : undefined}
      >
        {options.map((option) => {
          const isSelected = selected.includes(option.value);
          const iconName = getChipIconName(isSelected, multiple);

          return (
            <ChoiceChip
              key={option.id}
              label={option.label}
              clickable
              selected={isSelected}
              look={selectedChipLook}
              disabled={disabled}
              aria-pressed={isSelected}
              data-testid={option.testId}
              icon={iconName ? <DynamicIcon name={iconName} /> : undefined}
              onClick={() => toggle(option.value)}
            />
          );
        })}
        {multiple && clearable ? (
          <Button
            variant="text"
            size="small"
            color="secondary"
            disabled={disabled || selected.length === 0}
            onClick={() => onSelect([])}
            sx={{ textTransform: 'uppercase' }}
          >
            Clear
          </Button>
        ) : null}
      </Stack>
      {!!errorText && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};

const BoundChipGroup = <T extends ChipValue>(props: BoundChipGroupProps<T>) => {
  const { name, rules, required, showErrorText, showsIf, multiple, clearable, ...viewProps } =
    props;
  const { control, formState, getFieldState } = useFormContext();
  const errorMsg = getFieldState(name, formState).error?.message;
  const shouldShow = useWatchConditions({ control, showsIf });

  if (!shouldShow) {
    return null;
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{ ...rules, required }}
      render={({ field }) => (
        <ChipGroupView
          {...viewProps}
          selected={getSelectedValues(viewProps.options, field.value)}
          multiple={!!multiple}
          clearable={!!clearable}
          errorText={showErrorText && typeof errorMsg === 'string' ? errorMsg : undefined}
          onSelect={(next) => {
            field.onChange(multiple ? next : next[0]);
            emitChange(props, next);
          }}
        />
      )}
    />
  );
};

const ControlledChipGroup = <T extends ChipValue>(props: ControlledChipGroupProps<T>) => {
  const { value, multiple, clearable, ...viewProps } = props;

  return (
    <ChipGroupView
      {...viewProps}
      selected={getSelectedValues(viewProps.options, value)}
      multiple={!!multiple}
      clearable={!!clearable}
      onSelect={(next) => emitChange(props, next)}
    />
  );
};

export const FormChipGroup = <T extends ChipValue>(inProps: FormChipGroupProps<T>) => {
  const props = useThemeProps({ props: inProps, name: FORM_CHIP_GROUP_THEME_NAME });

  return props.name === undefined ? (
    <ControlledChipGroup {...props} />
  ) : (
    <BoundChipGroup {...props} />
  );
};
